import { useEffect, useMemo } from "react";

// MUI
import { Stack, Typography } from "@mui/material";

// Third Party
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { set } from "lodash-es";

// API
import { getDisabledAreaNumbersByStoreNumber } from "@Settings/api/AdministrationTools/queries.ts";
import { updateAdministrationToolsManagementSystem } from "@Settings/api/AdministrationTools/mutations.ts";

// Hooks
import { useLocationId } from "@Common/hooks/Location/useLocationId.tsx";

// Helpers
import { and } from "@Common/helpers/sonar.tsx";

// Components
import SimpleTextField from "@Layouts/components/InputComponents/SimpleTextField";
import SmartSelector from "@Layouts/components/InputComponents/SmartSelector";

// Helpers
import {
	checkAreaNumberError,
	checkIfDmsProvider,
	checkStoreNumberError,
	mapNumbers,
} from "@Settings/helpers/AdministrationTools/DealerAdministrationTools/DealerManagementSystemIds";

// Types
import {
	IAdministrationTools,
	IIntegration,
	IManagementSystem,
	TUpdateAdministrationToolsManagementSystemResponse,
} from "@Settings/types/administrationTools/model.ts";
import { EManagementSystem } from "@Settings/types/administrationTools/utils.ts";
import { useSubscriptions } from "@Common/context/subscriptions/Subscriptions";
import { MANAGEMENT_SYSTEM_SUBSCRIPTION } from "@Settings/api/AdministrationTools/subscriptions.ts";
import { GraphqlSubscriptionResponse } from "@Common/api/types/GraphqlSubscriptionResponse";

interface IDealerManagementDataSourceProps {
	integrations: IIntegration;
}

const DealerManagementSystemIds = (props: IDealerManagementDataSourceProps) => {
	const { addSubscription, getSubscription, deleteSubscription } = useSubscriptions();
	const queryClient = useQueryClient();
	const locationId = useLocationId();

	const { integrations } = props;
	const { dmsProvider } = integrations;

	/******************************************************* QUERIES ********************************************************/
	const {
		data: disabledAreaNumbersOptions,
		isLoading: isDisabledAreaNumbersOptionsLoading,
		refetch: refetchDisabledAreaNumbersOptions,
	} = useQuery({
		queryKey: ["getDisabledAreaNumbersByStoreNumber", locationId, EManagementSystem.DMS, dmsProvider?.storeNumber],
		queryFn: async () =>
			getDisabledAreaNumbersByStoreNumber(locationId as string, dmsProvider?.storeNumber as string, EManagementSystem.DMS),
		enabled: and(Boolean(locationId), Boolean(dmsProvider?.storeNumber)),
		staleTime: Infinity,
	});

	/******************************************************* MUTATIONS ******************************************************/
	const { mutate: mutateStoreNumber, isPending: isStoreNumberPending } = useMutation({
		mutationKey: ["DMSProviderStoreNumber", locationId],
		mutationFn: (input: IManagementSystem) =>
			updateAdministrationToolsManagementSystem(locationId as string, EManagementSystem.DMS, input),
		onMutate: (input: IManagementSystem) => {
			const previousValue = integrations?.dmsProvider?.storeNumber;
			const newValue = input.storeNumber;

			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.dmsProvider.storeNumber", newValue);
			});
			return { previousValue, newValue };
		},
		onError: (_err, _newValue, context) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.dmsProvider.storeNumber", context?.previousValue);
			});
		},
	});

	const { mutate: mutateAreaNumber, isPending: isAreaNumberPending } = useMutation({
		mutationKey: ["administrationToolsDmsAreaNumberId", locationId],
		mutationFn: (input: IManagementSystem) =>
			updateAdministrationToolsManagementSystem(locationId as string, EManagementSystem.DMS, input),
		onMutate: (input: IManagementSystem) => {
			const previousValue = integrations?.dmsProvider?.areaNumber;
			const newValue = input.areaNumber;

			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.dmsProvider.areaNumber", newValue);
			});
			return { previousValue, newValue };
		},
		onError: (_err, _newValue, context) => {
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools) => {
				return set(cachedData, "integrations.dmsProvider.areaNumber", context?.previousValue);
			});
		},
	});
	/******************************************************* SUBSCRIPTIONS **************************************************/

	const managementSystemSubscription = getSubscription([EManagementSystem.DMS, dmsProvider?.storeNumber]);
	managementSystemSubscription?.subscriptions?.onUpdateAdministrationToolsManagementSystem?.setCallback(
		(
			response: GraphqlSubscriptionResponse<
				"onUpdateAdministrationToolsManagementSystem",
				TUpdateAdministrationToolsManagementSystemResponse
			>
		) => {
			const updatedDmsProvider = response.data?.onUpdateAdministrationToolsManagementSystem;
			queryClient.setQueryData(["administrationTools", locationId], (cachedData: IAdministrationTools | undefined) => {
				if (!cachedData) {
					return cachedData;
				}

				const { locationId: updatedLocationId, managementSystem } =
					updatedDmsProvider as TUpdateAdministrationToolsManagementSystemResponse;

				if (updatedLocationId !== locationId) {
					return cachedData;
				}

				refetchDisabledAreaNumbersOptions();

				return {
					...cachedData,
					integrations: {
						...cachedData.integrations,
						dmsProvider: managementSystem,
					},
				};
			});
		}
	);
	/******************************************************* HANDLERS *******************************************************/

	const handleStoreNumberUpdate = (value: { label: string }) => {
		mutateStoreNumber({
			...dmsProvider,
			storeNumber: value.label,
			areaNumber: [],
		});
	};

	const handleAreaNumberUpdate = (newAreaNumbers: { label: string }[]) => {
		const newDmsProvider = {
			...dmsProvider,
			areaNumber: newAreaNumbers ? newAreaNumbers.map((item) => item.label) : [],
		};
		mutateAreaNumber(newDmsProvider);
	};

	const storeNumberOptions = useMemo(() => {
		const STORE_NUMBER_PAD_LENGTH = 3;
		return Array.from({ length: 999 }, (_, i) => ({
			label: String(i + 1).padStart(STORE_NUMBER_PAD_LENGTH, "0"),
		}));
	}, []);

	const areaNumberOptions = useMemo(() => {
		const AREA_NUMBER_PAD_LENGTH = 2;
		return Array.from({ length: 99 }, (_, i) => ({
			label: String(i + 1).padStart(AREA_NUMBER_PAD_LENGTH, "0"),
		}));
	}, []);

	useEffect(() => {
		addSubscription(
			[EManagementSystem.DMS, dmsProvider?.storeNumber],
			[
				{
					context: "onUpdateAdministrationToolsManagementSystem",
					query: MANAGEMENT_SYSTEM_SUBSCRIPTION,
					variables: {
						managementSystemType: EManagementSystem.DMS,
						storeNumber: dmsProvider?.storeNumber,
					},
				},
			]
		);
		return () => {
			deleteSubscription([EManagementSystem.DMS, dmsProvider?.storeNumber]);
		};
	}, [dmsProvider?.storeNumber]);

	if (!checkIfDmsProvider(dmsProvider?.providerName)) {
		return null;
	}

	return (
		<Stack display={"flex"} direction={"row"} gap={3} mb={1}>
			<Stack display={"flex"} direction={"column"} width={218}>
				<Typography className="!font-montserrat !text-[16px] !text-default !font-medium !mb-3">DMS ID</Typography>
				<SimpleTextField editable disabled outlined text={dmsProvider?.systemTenantId} placeholder={"DMS ID"} />
			</Stack>
			<Stack display={"flex"} direction={"row"} justifyContent={"flex-start"} gap={3}>
				<Stack display={"flex"} direction={"column"} width={218}>
					<Typography className="!font-montserrat !text-[16px] !text-default !font-medium !mb-3">Store ID</Typography>
					<SmartSelector
						showBy={"label"}
						selectBy={"label"}
						options={storeNumberOptions}
						selected={dmsProvider?.storeNumber && { label: dmsProvider?.storeNumber }}
						disabled={isStoreNumberPending}
						inputProps={{
							placeholder: "Stock Number(s)",
							classes: `[&_.MuiInputBase-root]:!py-[3px]`,
						}}
						renderTagsCustomProps={{
							classes: "!font-sans",
						}}
						onChangeAction={(value) => handleStoreNumberUpdate(value as { label: string })}
						error={checkStoreNumberError(dmsProvider?.storeNumber, storeNumberOptions)}
						classes="!w-[218px]"
						popperProps={{ className: "!z-[1350]" } as object}
					/>
				</Stack>
				<Stack display={"flex"} direction={"column"} width={218}>
					<Typography className="!font-montserrat !text-[16px] !text-default !font-medium !mb-3">Branch ID</Typography>
					<SmartSelector
						multiple
						showBy={"label"}
						selectBy={"label"}
						options={areaNumberOptions}
						disabledOptions={disabledAreaNumbersOptions ?? []}
						selected={dmsProvider?.areaNumber && mapNumbers(dmsProvider?.areaNumber)}
						disabled={isAreaNumberPending || isDisabledAreaNumbersOptionsLoading}
						loading={isDisabledAreaNumbersOptionsLoading}
						inputProps={{
							placeholder: "Stock Number(s)",
							classes: `[&_.MuiInputBase-root]:!py-[3px]`,
						}}
						renderTagsCustomProps={{
							classes: "!font-sans",
						}}
						onChangeAction={(value) => handleAreaNumberUpdate(value as { label: string }[])}
						error={checkAreaNumberError(integrations?.dmsProvider?.areaNumber, areaNumberOptions)}
						classes="!w-[218px]"
						popperProps={{ className: "!z-[1350]" } as object}
					/>
				</Stack>
			</Stack>
		</Stack>
	);
};

export default DealerManagementSystemIds;
